import { useEffect, useState } from "react";
import { ToastItem } from "./toastItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { Button } from "../Button";

interface Props {
  toast: ToastItem;
  onRemove: (id: string) => void;
}

const variantConfig = {
  success: {
    bg: "#38A169",
    progress: "bg-green-700",
    icon: "/icons/ic-success-modal.svg",
  },
  error: {
    bg: "#E43E3D",
    progress: "bg-red-700",
    icon: "/icons/ic-error-modal.svg",
  },
  warning: {
    bg: "#F17F1F",
    progress: "bg-orange-700",
    icon: "/icons/ic-warning-modal.svg",
  },
  info: {
    bg: "#4A90E4",
    progress: "bg-blue-700",
    icon: "/icons/ic-info-modal.svg",
  },
};

export function Toast({ toast, onRemove }: Props) {
  const [progress, setProgress] = useState(100);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const config = variantConfig[toast.variant];

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (toast.isAnnouncement) {
      return;
    }

    const start = Date.now();

    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const percent = Math.max(100 - (elapsed / toast.duration) * 100, 0);

      setProgress(percent);

      if (elapsed >= toast.duration) {
        handleClose();
      }
    }, 30);

    return () => clearInterval(interval);
  }, [toast.duration, toast.isAnnouncement]);

  const handleClose = () => {
    setIsClosing(true);

    setTimeout(() => {
      onRemove(toast.id);
    }, 300);
  };

  return (
    <div
      className={`
                relative w-80 sm:w-96 max-w-full
                rounded-xl shadow-lg
                p-4 pr-12
                text-white flex items-start gap-4
                overflow-hidden        
                transform transition-all duration-300 ease-in-out
                ${isVisible && !isClosing
          ? "translate-x-0 opacity-100"
          : "translate-x-full opacity-0"
        }
                hover:cursor-pointer
            `}
      style={{ backgroundColor: config.bg }}
      onClick={handleClose}
    >
      <Button
        onClick={handleClose}
        className="absolute top-3 right-3 size-5 rounded-full bg-white/80 text-gray-600 flex items-center justify-center hover:bg-white hover:scale-110 transition"
      >
        <FontAwesomeIcon icon={faTimes} size="xs" />
      </Button>

      <div className="w-12 h-12 flex-shrink-0 bg-white/90 rounded-xl flex items-center justify-center">
        <img src={config.icon} alt="icon" className="w-6 h-6 object-contain" />
      </div>

      <div className="flex-1 text-sm font-medium break-words flex items-center">
        {toast.message}
      </div>

      {!toast.isAnnouncement && (
        <div className="absolute bottom-0 left-0 w-full h-1 bg-white/30">
          <div
            className={`h-full ${config.progress}`}
            style={{
              width: `${progress}%`,
              transition: "width 30ms linear",
            }}
          />
        </div>
      )}
    </div>
  );
}
